/**
 * Soft Command OS view-model (127 Soft only · Soft≠Live).
 *
 * Normative product shape (local theatre — never invents Live):
 *   WORLD → SENSE → UNDERSTAND → EVIDENCE → DECIDE → DISPATCH
 *   → OBSERVE → REASSESS → ACT → AUDIT → LEARN
 *
 * Distinguishes:
 *   · plant  = fixed inventory ("what stands here")
 *   · mission = active Dispatch execution mark ("what is under AI order")
 *   · mobile idle = free unit available to be ordered
 *
 * Human is never out of the loop: override / stop / redirect policy flags.
 */

import { softObservePackOf, softRequestSnapshotAction } from "./soft-spatial-observe.js";

/** Standardized device posture for Command surfaces (display map). */
export const CMD_STATE = {
  ONLINE: "ONLINE",
  OFFLINE: "OFFLINE",
  IDLE: "IDLE",
  AVAILABLE: "AVAILABLE",
  BUSY: "BUSY",
  DISPATCHING: "DISPATCHING",
  EN_ROUTE: "EN_ROUTE",
  ARRIVED: "ARRIVED",
  EXECUTING: "EXECUTING",
  OBSERVING: "OBSERVING",
  RECORDING: "RECORDING",
  STREAMING: "STREAMING",
  AI_CONTROLLED: "AI_CONTROLLED",
  HUMAN_CONTROLLED: "HUMAN_CONTROLLED",
  OVERRIDDEN: "OVERRIDDEN",
  DOCKED: "DOCKED",
  ERROR: "ERROR",
};

/** Map layer: inventory plant vs mission (dispatch) mark. */
export function softMarkerPlaneOf(asset) {
  if (!asset || typeof asset !== "object") return "plant";
  if (asset.fixed || asset.kind === "device" || asset.routeProfile === "static") {
    return "plant";
  }
  if (asset.missionId && (!asset.free || asset.path)) return "mission";
  if (!asset.free && asset.missionId) return "mission";
  return "mobile";
}

export function softAssetCommandStateOf(asset, supervisor) {
  if (!asset) return CMD_STATE.ERROR;
  const sup = supervisor || {};
  if (sup.mode === "human" || sup.paused) {
    if (asset.missionId) return CMD_STATE.OVERRIDDEN;
    return CMD_STATE.HUMAN_CONTROLLED;
  }
  if (asset.fixed || asset.kind === "device") {
    return asset.surveyed ? CMD_STATE.OBSERVING : CMD_STATE.ONLINE;
  }
  if (asset.path && asset.path.length > 1) return CMD_STATE.EN_ROUTE;
  if (!asset.free && asset.missionId) return CMD_STATE.AI_CONTROLLED;
  if (asset.free) return CMD_STATE.AVAILABLE;
  return CMD_STATE.BUSY;
}

/** Build Dispatch objects from Soft missions (first-class · Soft≠Live). */
export function softDispatchesOf(state) {
  const s = state && typeof state === "object" ? state : {};
  if (Array.isArray(s.dispatches) && s.dispatches.length) {
    return s.dispatches.slice(0, 24);
  }
  /* Fallback: derive from missions when list not yet materialised. */
  const assets = s.assets || [];
  return (s.missions || []).slice(0, 12).map((m) => softDispatchFromMission(m, assets, s));
}

export function softDispatchFromMission(mission, assets, state) {
  const m = mission || {};
  const assigned = Array.isArray(m.assigned) ? m.assigned : [];
  const leadId = assigned[0] && assigned[0].id;
  const lead = (assets || []).find((a) => a && a.id === leadId) || null;
  const status =
    m.status === "soft_on_scene"
      ? "ARRIVED"
      : m.status === "enroute"
        ? "EN_ROUTE"
        : String(m.status || "EXECUTING").toUpperCase();
  return {
    id: String(m.id || ""),
    taskId: String(m.incidentId || m.id || ""),
    deviceIds: assigned.map((x) => String(x.id)),
    origin: lead && Array.isArray(lead.home) ? lead.home.slice() : null,
    destination: {
      deviceId: m.destDeviceId || (lead && lead.routeMeta && lead.routeMeta.destDeviceId) || null,
      place: m.place || "",
      ll: lead && lead.path && lead.path.length ? lead.path[lead.path.length - 1] : null,
    },
    objective: String(m.title || "Investigate Soft"),
    status,
    currentLl: lead && lead.ll ? lead.ll.slice() : null,
    routeProfile:
      (lead && lead.routeMeta && lead.routeMeta.profile) ||
      (assigned[0] && assigned[0].profile) ||
      "",
    evidenceN: Number(m.evidenceN) || 0,
    aiControlled: m.humanOverride !== true && !(state && state.supervisor && state.supervisor.paused),
    humanOverride: m.humanOverride === true || !!(state && state.supervisor && state.supervisor.paused),
    soft: true,
    startTicks: Number(m.t0 != null ? m.t0 : state && state.ticks) || 0,
  };
}

export function softActiveIncidentsOf(state) {
  const s = state || {};
  return (s.incidents || [])
    .filter((i) => i && i.status !== "soft_contained")
    .slice(0, 8)
    .map((i) => ({
      id: i.id,
      title: i.title,
      place: i.place || "",
      severity: i.severity || "med",
      status: i.status || "open",
      needs: i.needs || [],
      deviceId: i.deviceId || null,
      soft: true,
    }));
}

export function softEvidenceChainOf(state) {
  const s = state || {};
  if (Array.isArray(s.evidence) && s.evidence.length) return s.evidence.slice(0, 40);
  /* Ledger is the honest Soft audit spine until Live fabric lands. */
  return (s.ledger || []).slice(0, 40).map((row) => ({
    id: row.id,
    ts: row.ts,
    kind: row.kind,
    say: row.say,
    unit: row.unit || null,
    mission: row.mission || null,
    device: row.device || null,
    soft: true,
  }));
}

/**
 * Command Workbench snapshot — pure.
 * @param {object|null} state soft fleet sim state
 * @param {{ local?: boolean, livePlayerWired?: boolean, zoneScope?: object|null }} ctx
 */
export function softCommandWorkbenchOf(state, ctx) {
  const c = ctx && typeof ctx === "object" ? ctx : {};
  const local = c.local !== false;
  const player = c.livePlayerWired === true;
  const zoneScope = c.zoneScope && c.zoneScope.ok ? c.zoneScope : null;
  if (!state || !local) {
    return {
      ok: false,
      soft: true,
      honesty: "Soft command theatre is 127-only · never invents Live dispatch",
      supervisor: { mode: "sealed", paused: true },
      incidents: [],
      dispatches: [],
      evidence: [],
      planes: { plant: 0, mission: 0, mobile: 0 },
      zone: null,
      live: {
        browserPlayer: player ? "wired" : "not_wired",
        note: player
          ? "Browser Live path Live"
          : "RTSP/μDock path may exist · browser Live player Soft-honest not wired",
      },
      loop: COMMAND_LOOP_STEPS,
    };
  }

  const assets = state.assets || [];
  let plant = 0;
  let mission = 0;
  let mobile = 0;
  for (const a of assets) {
    const p = softMarkerPlaneOf(a);
    if (p === "plant") plant += 1;
    else if (p === "mission") mission += 1;
    else mobile += 1;
  }

  const supervisor = state.supervisor || { mode: "ai", paused: false, reason: "" };
  let dispatches = softDispatchesOf(state);
  let incidents = softActiveIncidentsOf(state);
  const evidence = softEvidenceChainOf(state);

  if (zoneScope && zoneScope.origin) {
    const r = zoneScope.radiusM || 95;
    const origin = zoneScope.origin;
    const nearSet = new Set(zoneScope.unitIds || []);
    dispatches = softNearFirst(
      dispatches,
      (d) => {
        if (d.currentLl) return d.currentLl;
        if (d.destination && d.destination.ll) return d.destination.ll;
        return null;
      },
      origin,
      r,
      (d) => (d.deviceIds || []).some((id) => nearSet.has(String(id))),
    );
    incidents = softNearFirst(
      incidents,
      (i) => {
        const full = (state.incidents || []).find((x) => x && x.id === i.id);
        return full && full.ll ? full.ll : null;
      },
      origin,
      r,
      (i) =>
        String(i.place || "")
          .toLowerCase()
          .includes(String(zoneScope.label || "").toLowerCase().slice(0, 8)),
    );
  }

  const camsOpen = dispatches.filter(
    (d) =>
      String(d.objective || "").toLowerCase().includes("cam") ||
      String(d.destination && d.destination.place || "").toLowerCase().includes("cam"),
  ).length;
  const observe = softObservePackOf(
    state,
    zoneScope
      ? {
          origin: zoneScope.origin,
          preferCamIds: zoneScope.cameraIds,
          radiusM: Math.max(120, zoneScope.radiusM || 95),
          zoneId: zoneScope.zoneId,
          zoneLabel: zoneScope.label,
        }
      : {},
  );
  const multiN =
    (observe.multiView && observe.multiView.cameras && observe.multiView.cameras.length) || 0;

  return {
    ok: true,
    soft: true,
    honesty: zoneScope
      ? `Zone ops · ${zoneScope.label} · Soft≠Live · plant ≠ mission`
      : "Soft≠Live · inventory plant ≠ mission dispatch · map never forges Live orders",
    zone: zoneScope
      ? {
          id: zoneScope.zoneId,
          label: zoneScope.label,
          cams: (zoneScope.cameraIds || []).length,
          units: (zoneScope.unitIds || []).length,
          radiusM: zoneScope.radiusM,
        }
      : null,
    supervisor: {
      mode: supervisor.paused || supervisor.mode === "human" ? "human" : "ai",
      paused: !!supervisor.paused,
      reason: supervisor.reason || "",
      label:
        supervisor.paused || supervisor.mode === "human"
          ? "HUMAN OVERRIDE"
          : zoneScope
            ? "AI · ZONE SCOPE Soft"
            : "AI SUPERVISOR · Soft",
    },
    kpis: {
      incidents: incidents.length,
      dispatches: dispatches.filter((d) => d.status === "EN_ROUTE" || d.status === "ARRIVED").length,
      plant,
      mission,
      mobileReady: mobile,
      evidence: evidence.length,
      softDispatches: Number(state.softDispatches) || 0,
      scene: state.scene || "perimeter",
      multiView: multiN,
      zoneUnits: zoneScope ? (zoneScope.unitIds || []).length : 0,
    },
    incidents,
    dispatches,
    evidence,
    planes: { plant, mission, mobile },
    observe,
    live: {
      browserPlayer: player ? "wired" : "not_wired",
      softCamsHint: camsOpen,
      multiViewN: multiN,
      note: player
        ? "Browser Live path Live"
        : "μDock / encode path may be Live · browser multi-cam player Soft gap · Soft multi-view tiles only",
    },
    loop: COMMAND_LOOP_STEPS,
    focusPolicy:
      "Map uses Focus highlight · no thrash zoom on each Soft event · operator Focus or idle AI tour only",
  };
}

/** Prefer near-zone items; always leave 1–2 global tails so theatre never ghosts. */
function softNearFirst(items, getLl, origin, radiusM, fallbackMatch) {
  if (!Array.isArray(items) || !origin) return items || [];
  const near = [];
  const far = [];
  for (const it of items) {
    const ll = getLl ? getLl(it) : null;
    const inR = ll && haversineLite(origin, ll) <= radiusM;
    const fb = fallbackMatch ? fallbackMatch(it) : false;
    if (inR || fb) near.push(it);
    else far.push(it);
  }
  if (!near.length) return items.slice(0, 12);
  return [...near, ...far.slice(0, 2)].slice(0, 16);
}

function haversineLite(a, b) {
  if (!a || !b) return 1e9;
  const R = 6371000;
  const toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b[1] - a[1]);
  const dLng = toR(b[0] - a[0]);
  const la1 = toR(a[1]);
  const la2 = toR(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export const COMMAND_LOOP_STEPS = [
  { id: "sense", label: "Sense" },
  { id: "understand", label: "Understand" },
  { id: "evidence", label: "Evidence" },
  { id: "decide", label: "Mission" },
  { id: "dispatch", label: "Dispatch" },
  { id: "observe", label: "Observe" },
  { id: "reassess", label: "Reassess" },
  { id: "audit", label: "Audit" },
];

/** Human override reducing Soft planner + free enroute units. */
export function softHumanStopAll(state, reason) {
  if (!state) return state;
  const say = reason || "Human STOP ALL · Soft theatre";
  let assets = (state.assets || []).map((a) => {
    if (!a || a.fixed || a.kind === "device") return a;
    if (!a.missionId && a.free) return a;
    return {
      ...a,
      free: true,
      missionId: null,
      path: null,
      pathI: 0,
      etaS: 0,
      routeMeta: a.routeMeta
        ? { ...a.routeMeta, humanStop: true }
        : { humanStop: true },
    };
  });
  const dispatches = softDispatchesOf(state).map((d) => ({
    ...d,
    status: "OVERRIDDEN",
    aiControlled: false,
    humanOverride: true,
  }));
  const missions = (state.missions || []).map((m) => ({
    ...m,
    status: "human_stop",
    humanOverride: true,
  }));
  const ledger = [
    {
      id: `L${state.ticks}-HSTOP`,
      ts: new Date().toTimeString().slice(0, 8),
      kind: "HUMAN_OVERRIDE",
      say,
    },
    ...(state.ledger || []),
  ].slice(0, 40);
  return {
    ...state,
    assets,
    missions,
    dispatches,
    planning: false,
    supervisor: {
      mode: "human",
      paused: true,
      reason: say,
    },
    ledger,
  };
}

/**
 * Stop Soft mobiles inside zone radius only · AI elsewhere continues.
 * Soft theatre · never Live commands.
 */
export function softHumanStopInZone(state, origin, radiusM, reason) {
  if (!state || !origin) return softHumanStopAll(state, reason);
  const r =
    Number.isFinite(Number(radiusM)) && Number(radiusM) > 0 ? Number(radiusM) : 95;
  const say = reason || "Human STOP · zone Soft";
  const inZone = (ll) => ll && haversineLite(origin, ll) <= r;
  let assets = (state.assets || []).map((a) => {
    if (!a || a.fixed || a.kind === "device") return a;
    if (!inZone(a.ll)) return a;
    if (!a.missionId && a.free) return a;
    return {
      ...a,
      free: true,
      missionId: null,
      path: null,
      pathI: 0,
      etaS: 0,
      routeMeta: a.routeMeta
        ? { ...a.routeMeta, humanStop: true, zoneStop: true }
        : { humanStop: true, zoneStop: true },
    };
  });
  const dispatches = softDispatchesOf(state).map((d) => {
    const ll = d.currentLl || (d.destination && d.destination.ll);
    if (!inZone(ll) && !(d.deviceIds || []).some((id) => {
      const a = assets.find((x) => x && x.id === id);
      return a && inZone(a.ll);
    })) {
      return d;
    }
    return {
      ...d,
      status: "OVERRIDDEN",
      aiControlled: false,
      humanOverride: true,
    };
  });
  const ledger = [
    {
      id: `L${state.ticks}-ZSTOP`,
      ts: new Date().toTimeString().slice(0, 8),
      kind: "HUMAN_ZONE_STOP",
      say,
    },
    ...(state.ledger || []),
  ].slice(0, 40);
  /* Zone stop does not fully pause AI — only free units near zone. Soft≠Live. */
  return {
    ...state,
    assets,
    dispatches,
    ledger,
    supervisor: {
      ...(state.supervisor || {}),
      mode: "ai",
      paused: false,
      reason: say,
    },
  };
}

export function softHumanResumeAi(state) {
  if (!state) return state;
  const ledger = [
    {
      id: `L${state.ticks}-HRES`,
      ts: new Date().toTimeString().slice(0, 8),
      kind: "HUMAN_RESUME",
      say: "Human resume · AI supervisor Soft",
    },
    ...(state.ledger || []),
  ].slice(0, 40);
  return {
    ...state,
    supervisor: { mode: "ai", paused: false, reason: "" },
    ledger,
  };
}

export function softHumanStopDispatch(state, dispatchId) {
  if (!state || !dispatchId) return state;
  const id = String(dispatchId);
  const hit = softDispatchesOf(state).find((d) => String(d.id) === id);
  if (!hit) return state;
  const unitSet = new Set((hit.deviceIds || []).map(String));
  let assets = (state.assets || []).map((a) => {
    if (!a || !unitSet.has(String(a.id))) return a;
    return {
      ...a,
      free: true,
      missionId: null,
      path: null,
      pathI: 0,
      etaS: 0,
    };
  });
  const dispatches = softDispatchesOf(state).map((d) =>
    String(d.id) === id
      ? { ...d, status: "OVERRIDDEN", aiControlled: false, humanOverride: true }
      : d,
  );
  const missions = (state.missions || []).map((m) =>
    String(m.id) === id ? { ...m, status: "human_stop", humanOverride: true } : m,
  );
  const ledger = [
    {
      id: `L${state.ticks}-H1`,
      ts: new Date().toTimeString().slice(0, 8),
      kind: "HUMAN_OVERRIDE",
      say: `Human STOP dispatch ${id} · Soft`,
      mission: id,
    },
    ...(state.ledger || []),
  ].slice(0, 40);
  return { ...state, assets, missions, dispatches, ledger };
}

/** Human changes Soft objective / destination label without Live write. */
export function softHumanRedirect(state, dispatchId, objective) {
  if (!state || !dispatchId) return state;
  const id = String(dispatchId);
  const obj = String(objective || "Observe only · Soft").slice(0, 120);
  const missions = (state.missions || []).map((m) =>
    String(m.id) === id
      ? { ...m, title: obj, humanOverride: true, status: m.status || "enroute" }
      : m,
  );
  const dispatches = softDispatchesOf({ ...state, missions }).map((d) =>
    String(d.id) === id
      ? {
          ...d,
          objective: obj,
          humanOverride: true,
          aiControlled: false,
        }
      : d,
  );
  const ledger = [
    {
      id: `L${state.ticks}-HREDIR`,
      ts: new Date().toTimeString().slice(0, 8),
      kind: "HUMAN_REDIRECT",
      say: `Human redirect · ${id} → ${obj} · Soft`,
      mission: id,
    },
    ...(state.ledger || []),
  ].slice(0, 40);
  return { ...state, missions, dispatches, ledger };
}

/** Soft REQUEST_SNAPSHOT · evidence seed (no Live blob invent). */
export function softRequestSnapshot(state, cameraId) {
  if (!state || !cameraId) return state;
  const act = softRequestSnapshotAction(cameraId, { ticks: state.ticks });
  const row = {
    id: `L${state.ticks}-SNAP`,
    ts: new Date().toTimeString().slice(0, 8),
    kind: act.kind,
    say: act.say,
    unit: cameraId,
    device: cameraId,
  };
  const ledger = [row, ...(state.ledger || [])].slice(0, 40);
  const evidence = [
    {
      id: row.id,
      ts: row.ts,
      kind: row.kind,
      say: row.say,
      unit: cameraId,
      device: cameraId,
      soft: true,
      content: "soft_snapshot_ref",
    },
    ...(state.evidence || []),
  ].slice(0, 48);
  return { ...state, ledger, evidence };
}
